import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import JoinNowFormPopup from "../../../componants/JoinNowFormPopup";

// Helper component for Star Rating (reused)
const StarRating = ({ rating }: { rating: number }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <svg
        key={i}
        className={`h-4 w-4 ${
          i <= rating ? "text-yellow-400" : "text-gray-300"
        } inline-block`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.783.57-1.838-.197-1.538-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.381-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
      </svg>
    );
  }
  return <div className="flex items-center">{stars}</div>;
};

// Helper component for Verified Badge (reused)
const VerifiedBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 ml-2">
    <svg
      className="w-3 h-3 mr-1"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
    Verified
  </span>
);

const SMMExpert = () => {
  const testimonials = [
    {
      id: 1,
      name: "Shruti Sharma",
      review:
        "This SMM Expert course was fantastic! I learned how to create engaging content, run targeted ad campaigns, and analyze social media performance effectively. My brand's online presence has soared!",
      rating: 5,
      date: "2024-06-10",
      profileImg: "https://randomuser.me/api/portraits/women/35.jpg", // Placeholder image
      source: "Google Reviews",
    },
    {
      id: 2,
      name: "Arjun Singh",
      review:
        "The trainers are industry experts, and the practical sessions on Instagram and Facebook Ads were invaluable. I now manage social media for multiple clients, thanks to this comprehensive training in Noida.",
      rating: 5,
      date: "2024-06-08",
      profileImg: "https://randomuser.me/api/portraits/men/45.jpg", // Placeholder image
      source: "LinkedIn",
    },
    {
      id: 3,
      name: "Ishita Verma",
      review:
        "I was completely new to social media marketing, but this course made it easy to understand. From content calendars to influencer marketing, every aspect was covered thoroughly. Highly recommend!",
      rating: 4,
      date: "2024-06-05",
      profileImg: "https://randomuser.me/api/portraits/women/55.jpg", // Placeholder image
      source: "Website Feedback",
    },
    {
      id: 4,
      name: "Rohan Khanna",
      review:
        "The SMM course content is highly relevant and updated with the latest trends. I particularly enjoyed learning about community management and crisis handling. Great value for money and excellent placement support.",
      rating: 5,
      date: "2024-06-02",
      profileImg: "https://randomuser.me/api/portraits/men/65.jpg", // Placeholder image
      source: "Google Reviews",
    },
    {
      id: 5,
      name: "Meera Devi",
      review:
        "This training equipped me with the skills to confidently manage social media for small businesses. The focus on analytics and ROI was very practical. A must-do course for aspiring social media managers.",
      rating: 4,
      date: "2024-05-30",
      profileImg: "https://randomuser.me/api/portraits/women/75.jpg", // Placeholder image
      source: "LinkedIn",
    },
  ];

  const [isPopupOpen, setIsPopupOpen] = useState(false); // State to control popup visibility
      const [selectedCourseTitle, setSelectedCourseTitle] = useState(""); // State to store the course title
    
      const handleOpenPopup = (courseTitle: string) => { // Function to open the popup
        setSelectedCourseTitle(courseTitle); // Set the selected course title
        setIsPopupOpen(true); // Open the popup
      };
    
      const handleClosePopup = () => { // Function to close the popup
        setIsPopupOpen(false); // Close the popup
        setSelectedCourseTitle(""); // Clear selected course when closing
      };

  const faqContent = [
    {
      title: "What is SMM/SMH and why is it important for businesses?",
      answer:
        "SMM (Social Media Marketing) and SMH (Social Media Handling) involve using social media platforms to connect with your audience, build brand awareness, drive website traffic, and increase sales. It's crucial because billions of people use social media daily, offering an unparalleled opportunity for direct engagement, targeted advertising, and brand building.",
    },
    {
      title: "What are the prerequisites for this SMM Expert course?",
      answer:
        "No specific technical prerequisites are required. A basic understanding of social media platforms as a user is helpful. This course is designed for aspiring digital marketers, content creators, entrepreneurs, and anyone looking to leverage social media for business growth.",
    },
    {
      title: "What key social media platforms and concepts will I master?",
      answer:
        "You will gain expertise in **Facebook, Instagram, LinkedIn, Twitter, YouTube**, and more. Key concepts include **Content Strategy, Community Management, Social Media Advertising (Paid Ads), Analytics & Reporting, Influencer Marketing, Social Listening**, and **Crisis Management**.",
    },
    {
      title: "Is there a high demand for SMM professionals?",
      answer:
        "Yes, the demand for skilled Social Media Marketing and Handling professionals is booming. Businesses are increasingly investing in social media to connect with customers and drive growth, creating numerous opportunities for experts who can craft compelling strategies and manage engaging online presences.",
    },
    {
      title: "Will I get placement assistance after completing the course?",
      answer:
        "Absolutely! We provide dedicated placement assistance for our SMM graduates. This includes professional resume building, mock interview preparation, soft skills training, and connecting you with leading digital marketing agencies and companies in Noida and across India for roles like Social Media Manager, Digital Marketing Executive, and Content Creator.",
    },
    {
      title: "What kind of practical projects will I work on?",
      answer:
        "You will work on live social media campaigns, developing content calendars, creating engaging posts, setting up and optimizing paid ad campaigns, analyzing social media analytics, and even developing a complete social media strategy for a mock or real business. This hands-on experience is vital.",
    },
    {
      title: "What are the career opportunities after this SMM training?",
      answer:
        "Upon successful completion, you can pursue exciting careers as a Social Media Manager, Social Media Executive, Digital Marketing Specialist, Content Creator, Community Manager, Social Media Strategist, or a freelance Social Media Consultant.",
    },
    {
      title: "What is the duration and mode of training?",
      answer:
        "The course duration varies based on the pace (intensive fast-track vs. regular batch). We offer both immersive offline classroom training at our Noida center and interactive live online classes, providing flexibility to suit your learning preferences.",
    },
  ];

  const curriculumContent = [
    {
      title: "Module 1: Introduction to Social Media Marketing",
      description: "Understanding the social media landscape, setting SMM goals, identifying target audiences, and developing a holistic social media strategy.",
    },
    {
      title: "Module 2: Content Strategy & Creation for Social Media",
      description: "Crafting engaging content, visual storytelling, video content, content calendars, and using tools for content creation.",
    },
    {
      title: "Module 3: Facebook Marketing & Advertising",
      description: "Building Facebook Pages, managing communities, Facebook Ads Manager, audience targeting, campaign objectives, and ad formats.",
    },
    {
      title: "Module 4: Instagram Marketing & Growth",
      description: "Optimizing Instagram profiles, content strategies for Stories & Reels, hashtag research, Instagram Ads, and influencer collaborations.",
    },
    {
      title: "Module 5: LinkedIn Marketing for Professionals & Businesses",
      description: "Leveraging LinkedIn for personal branding, company pages, B2B lead generation, LinkedIn Ads, and professional networking.",
    },
    {
      title: "Module 6: Twitter Marketing & Real-time Engagement",
      description: "Crafting effective tweets, managing Twitter conversations, using hashtags, Twitter Ads, and crisis communication on Twitter.",
    },
    {
      title: "Module 7: YouTube Marketing & Video SEO",
      description: "Channel optimization, video content strategy, YouTube SEO (titles, descriptions, tags), YouTube Ads, and building subscribers.",
    },
    {
      title: "Module 8: Social Media Analytics & Reporting",
      description: "Using native analytics tools (Facebook Insights, Instagram Insights), Google Analytics for social traffic, and creating performance reports.",
    },
    {
      title: "Module 9: Influencer Marketing & Collaborations",
      description: "Identifying relevant influencers, negotiation strategies, campaign execution, and measuring influencer ROI.",
    },
    {
      title: "Module 10: Community Management & ORM",
      description: "Engaging with followers, managing comments and messages, online reputation management (ORM), and handling negative feedback.",
    },
    {
      title: "Module 11: Social Media Tools & Automation",
      description: "Exploring scheduling tools (Buffer, Hootsuite), design tools (Canva), and advanced analytics platforms.",
    },
    {
      title: "Module 12: Live Project & Campaign Execution",
      description: "Develop and execute a complete social media marketing strategy for a real or mock business, including content creation, ad campaigns, and performance analysis.",
    },
  ];

  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);

  useEffect(() => {
    const autoScrollInterval = setInterval(() => {
      setCurrentTestimonialIndex(
        (prevIndex) => (prevIndex + 1) % testimonials.length
      );
    }, 5000); // Auto-slide every 5 seconds

    return () => clearInterval(autoScrollInterval); // Clean up the interval
  }, [testimonials.length]);

  const goToPrevTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const goToNextTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      (prevIndex + 1) % testimonials.length
    );
  };

  const getVisibleTestimonialsCount = () => {
    if (window.innerWidth >= 1024) return 3; // lg breakpoint
    if (window.innerWidth >= 768) return 2; // md breakpoint
    return 1; // default for small screens
  };

  const [visibleCount, setVisibleCount] = useState(
    getVisibleTestimonialsCount()
  );

  useEffect(() => {
    const handleResize = () => {
      setVisibleCount(getVisibleTestimonialsCount());
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const startIndex = currentTestimonialIndex;
  const currentVisibleTestimonials = [];

  for (let i = 0; i < visibleCount; i++) {
    currentVisibleTestimonials.push(
      testimonials[(startIndex + i) % testimonials.length]
    );
  }

  return (
    <div className="bg-white text-gray-800">
      <Helmet>
        <title>SMM Expert Course in Noida | Social Media Marketing Training</title>
        <meta
          name="description"
          content="Become a certified SMM/SMH Expert in Noida. Master social media strategy, content creation, paid campaigns (Facebook, Instagram), analytics, and community management with placement support."
        />
        <meta
          name="keywords"
          content="SMM expert course Noida, Social Media Marketing training, Social Media Handling, Facebook Ads, Instagram Marketing, LinkedIn Marketing, YouTube Marketing, Digital Marketing, Social Media Strategy, SMM certification, best SMM course"
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden text-white h-[400px] bg-gray-900 flex items-center">
        <img
          src="https://t4.ftcdn.net/jpg/05/23/45/11/360_F_523451109_w8g1pFrGEiRKmlecmfEmPWOnqwy5fE2v.jpg"
          alt="SMM Expert Course Background"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />

        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-60 z-10" />

       <div className="relative z-10 w-full px-6 py-32 md:py-40 flex justify-end">
  <div className="w-full md:w-1/2 flex flex-col items-end text-right">
    <motion.h1
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      className="text-4xl md:text-5xl font-extrabold leading-tight text-white"
    >
      Become an <span className="text-blue-400">SMM Expert</span>
    </motion.h1>

    <motion.p
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3, duration: 0.8 }}
      className="mt-6 text-lg md:text-xl text-gray-200"
    >
      Master social media marketing, content creation, and paid advertising to build powerful online brands.
    </motion.p>

    <motion.button
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6, duration: 0.8 }}
      className="mt-8 bg-blue-700 hover:bg-blue-600 text-white font-bold py-3 px-8 rounded-full shadow-lg transition duration-300 ease-in-out"
    >
      <button
                    // className="bg-orange-600 hover:bg-orange-700 px-6 py-3 rounded-lg font-semibold transition text-white"
                    onClick={() => handleOpenPopup("SMM Expert Training")} // Pass the course title
                  >
                    Enroll Now !
                  </button>
    </motion.button>
  </div>
</div>

      </section>

      {/* Why Learn SMM Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Why Become an SMM Expert?</h2>
          <p className="text-lg text-gray-700 mb-8 max-w-3xl mx-auto">
            Social media is where your audience lives, making SMM a vital skill for any business. As an SMM expert, you'll craft engaging strategies, connect brands with their customers, drive community growth, and directly impact marketing ROI.
          </p>
          <div className="grid md:grid-cols-3 gap-8 mt-10">
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-green-700 mb-3">Massive Audience Reach</h3>
              <p className="text-gray-600">Connect with billions of potential customers globally.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-green-700 mb-3">High Demand Skill</h3>
              <p className="text-gray-600">Businesses are constantly investing in strong social media presence.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-green-700 mb-3">Creative & Dynamic Career</h3>
              <p className="text-gray-600">Combine strategy, creativity, and data analysis in a fast-paced field.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Course Highlights Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-blue-900">Course Highlights</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: '📱', title: 'Multi-Platform Mastery', desc: 'Expertise in Facebook, Instagram, LinkedIn, Twitter, YouTube, and more.' },
              { icon: '✍️', title: 'Content & Strategy Creation', desc: 'Develop engaging social media content plans and execution strategies.' },
              { icon: '💸', title: 'Paid Social Media Advertising', desc: 'Master Facebook & Instagram Ads Manager for targeted campaigns.' },
              { icon: '👨‍🏫', title: 'Industry Expert Trainers', desc: 'Learn from seasoned social media managers and digital marketers.' },
              { icon: '🎓', title: 'Certification & Portfolio Building', desc: 'Receive a professional certificate and build a strong SMM portfolio.' },
              { icon: '🤝', title: '100% Placement Assistance', desc: 'Dedicated support for resume building, mock interviews, and job placements.' },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-green-50 p-6 rounded-lg shadow-md flex items-start space-x-4"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-4xl">{item.icon}</div>
                <div>
                  <h3 className="font-semibold text-xl text-green-800 mb-2">{item.title}</h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum Overview Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-blue-900">Detailed Curriculum Overview</h2>
          <div className="space-y-4">
            {curriculumContent.map((module, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100 transition duration-200 ease-in-out flex justify-between items-center">
                    {module.title}
                    <span className="transform transition-transform duration-200 group-open:rotate-180">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 py-4 text-gray-700 leading-relaxed border-t border-gray-200">
                    <p>{module.description}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools & Platforms */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Tools & Platforms You'll Master</h2>
          <p className="text-lg text-gray-700 mb-10 max-w-3xl mx-auto">
            Our SMM Expert training provides extensive hands-on experience with the leading social media platforms and marketing tools.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center justify-center">
            {[
              { name: 'Facebook', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/facebook/facebook-original.svg', desc: 'Pages, Groups, Ads Manager' },
              { name: 'Instagram', logo: 'https://img.freepik.com/premium-vector/purple-gradiend-social-media-logo_197792-1883.jpg', desc: 'Content, Ads, Insights' },
              { name: 'LinkedIn', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/linkedin/linkedin-original.svg', desc: 'Personal & Business Branding' },
              { name: 'Twitter (X)', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/twitter/twitter-original.svg', desc: 'Real-time marketing' },
              { name: 'YouTube', logo: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTKfPimrl1lwgrW3OHOuuyReVPqtc_Hd7mp4A&s', desc: 'Video marketing & SEO' },
              { name: 'Canva', logo: 'https://521324.fs1.hubspotusercontent-na1.net/hubfs/521324/__hs-marketplace__/Canva_Circle_Gradient%20(1).jpeg', desc: 'Graphic design for social media' },
              
              { name: 'Google Analytics 4', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/google/google-original.svg', desc: 'Traffic & conversion tracking' },
             
            ].map((tool, index) => (
              <motion.div
                key={index}
                className="flex flex-col items-center p-4 bg-gray-100 rounded-lg shadow-sm text-center"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <img src={tool.logo} alt={tool.name} className="h-12 mb-2 object-contain" />
                <p className="text-gray-700 font-medium text-sm">{tool.name}</p>
                {tool.desc && <p className="text-gray-500 text-xs mt-1">{tool.desc}</p>}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Opportunities */}
      <section className="py-16 bg-green-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Exciting Career Opportunities in SMM</h2>
          <p className="text-lg text-gray-700 mb-10 max-w-3xl mx-auto">
            A certification as an **SMM Expert** will open doors to a dynamic career in digital marketing, where you can shape brand narratives and engage millions.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: '✨', title: 'Social Media Manager', desc: 'Develop and execute comprehensive social media strategies.' },
              { icon: '✍️', title: 'Content Creator (Social Media)', desc: 'Produce engaging text, image, and video content for various platforms.' },
              { icon: '🗣️', title: 'Community Manager', desc: 'Build and nurture online communities, fostering brand loyalty.' },
              { icon: '💸', title: 'Social Media Advertiser', desc: 'Manage and optimize paid social media campaigns for ROI.' },
              { icon: '📈', title: 'Digital Marketing Executive', desc: 'Oversee various digital marketing channels, with a strong SMM focus.' },
              { icon: '💡', title: 'Freelance Social Media Consultant', desc: 'Offer specialized SMM services to multiple businesses.' },
            ].map((role, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 flex flex-col items-center text-center"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-5xl mb-4">{role.icon}</div>
                <h3 className="font-semibold text-xl text-green-800 mb-2">{role.title}</h3>
                <p className="text-gray-700">{role.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="max-w-6xl mx-auto px-4 py-16 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center md:text-left">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqContent.map((section, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100 transition duration-200 ease-in-out flex justify-between items-center">
                    {section.title}
                    <span className="transform transition-transform duration-200 group-open:rotate-180">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 py-4 text-gray-700 leading-relaxed border-t border-gray-200">
                    <p>{section.answer}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>

        {/* Enquiry Form */}
        <div className="md:col-span-1 pt-20">
          <div className="bg-blue-300 rounded-lg p-6 shadow-xl">
            <h3 className="text-2xl font-bold mb-4 text-center text-blue-800">
              Enquire Now
            </h3>
            <p className="text-center text-gray-600 mb-6">
              Fill out the form below to get more details about our SMM/SMH Expert training program.
            </p>
            <form className="space-y-4">
              <input
                type="text"
                placeholder="Your Name"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="text"
                placeholder="Course of Interest (e.g., SMM Expert Course)"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="text"
                placeholder="College/University Name (Optional)"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="email"
                placeholder="Your Email"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="tel"
                placeholder="Phone Number"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <button
                type="submit"
                className="w-full bg-blue-800 hover:bg-blue-700 text-white py-3 rounded-md font-semibold transition duration-300 ease-in-out shadow-md hover:shadow-lg"
              >
                JOIN THE PROGRAM
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Testimonial Section - No Horizontal Scrollbar */}
      <section className="bg-green-50 py-16">
        <div className="max-w-7xl mx-auto px-4 text-center relative">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-green-900">
            What Our SMM Expert Students Say
          </h2>

          <div className="relative overflow-hidden w-full mx-auto">
            {/* Left Scroll Button */}
            <button
              onClick={goToPrevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300 hidden md:block"
              aria-label="Previous testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                ></path>
              </svg>
            </button>

            {/* Testimonial Cards Container */}
            <div className="flex justify-center items-stretch space-x-6 md:space-x-8 lg:space-x-10">
              <AnimatePresence mode="wait" initial={false}>
                {currentVisibleTestimonials.map((testimonial, idx) => (
                  <motion.div
                    key={testimonial.id}
                    initial={{ opacity: 0, x: idx * 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="flex-none w-full md:w-[calc(50%-1.5rem)] lg:w-[calc(33.333%-2rem)] p-2"
                  >
                    <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 flex flex-col h-full text-left">
                      <div className="flex items-center mb-4">
                        <img
                          src={testimonial.profileImg}
                          alt={testimonial.name}
                          className="w-12 h-12 rounded-full object-cover mr-4 border-2 border-green-200"
                        />
                        <div>
                          <p className="font-bold text-gray-900 text-lg">
                            {testimonial.name}
                          </p>
                          <p className="text-gray-500 text-sm">
                            {testimonial.date} • {testimonial.source}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center mb-3">
                        <StarRating rating={testimonial.rating} />
                        {testimonial && <VerifiedBadge />}
                      </div>
                      <p className="text-gray-700 text-base leading-relaxed flex-grow line-clamp-4">
                        {testimonial.review}
                      </p>
                      <a
                        href="#"
                        className="text-green-600 hover:underline mt-4 text-sm text-right"
                      >
                        Read more
                      </a>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Right Scroll Button */}
            <button
              onClick={goToNextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300 hidden md:block"
              aria-label="Next testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                ></path>
              </svg>
            </button>
          </div>

          {/* Dot Indicators */}
          <div className="flex justify-center mt-8 space-x-2">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                className={`h-3 w-3 rounded-full ${
                  idx >= currentTestimonialIndex &&
                  idx < currentTestimonialIndex + visibleCount
                    ? "bg-green-600"
                    : "bg-gray-300"
                } transition-colors duration-300`}
                onClick={() => setCurrentTestimonialIndex(idx)}
                aria-label={`Go to testimonial group ${idx + 1}`}
              ></button>
            ))}
          </div>
        </div>
      </section>

       {/* Conditionally render the popup */}
      {isPopupOpen && (
        <JoinNowFormPopup
          onClose={handleClosePopup}
          courseTitle={selectedCourseTitle}
        />
      )}
    </div>
  );
};

export default SMMExpert;